import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Row, Col, Select, Table, Spin, message, Modal, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useScrollFetch2 } from "hooks/useScrollFetch2";
import { useFetch } from "hooks/useFetch";
import { find, uniqBy } from "lodash";
import { useHistory } from "react-router-dom";
import queryKeys from "common/queryKey";
import { getUserInfo, getUserList, getCRMManagingOfficeList, getHigherOfficials, getCountryFilterResourcePlanningList } from "redux/selectors";
import styled from "styled-components";
import Icon from "atoms/Icon";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import Title from "atoms/Title";
import { setselectedengagementtab, setTempEngagementFormData, setAccessibleEngagementIds } from "redux/actions";
import { getEmailsByRole } from "utils/functions";


const { Option } = Select;

const EngagementWrapper = styled.div`
  .ant-table-body {
    min-height: 300px;
    max-height: 600px !important;
  }
  @media (max-width: 1324px) {
    .ant-table-body {
      min-height: 200px;
      max-height: 300px !important;
    }
  }
  .title {
    display: flex;
    justify-content: space-between;
  }

  .search-box {
    max-width: 250px;
    margin-bottom: 8px;
  }

  .green-dot {
    height: 15px;
    content: "";
    width: 22px;
    background-color: #4caf50;
    border-radius: 9999px;
    display: inline-block;
  }
  .table-row {
    cursor: pointer;
  }

  .custom-filter-btn {
    margin-bottom: 0px !important;
  }

  .pagination-loader {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const RoundedSelect = styled(Select)`
  .ant-select-selector {
    border-radius: 40px !important;
  }
`;
const EngagementCheckNew = () => {
    const [form] = Form.useForm();
    const [filters, setFilters] = useState({
        jobTitle: [],
        clientName: [],
        personIncharge: [],
        partner: [],
        competency: [],
        serviceLine: [],
        country: [],
        engagementStatus: [],
        pfaStatus: [],
    });
    const [selectedFilters, setSelectedFilters] = useState({});
    // Tracks whether the user has explicitly modified the Title filter (as opposed to
    // the initial mount seeding engagementStatus defaults, which never touches jobTitle
    // and doesn't fire onValuesChange anyway). Read (not depended on) inside the
    // queryString useMemo below.
    const titleModifiedRef = useRef(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [localData, setLocalData] = useState([]);
    const [cplMap, setCplMap] = useState({});         // { engagementId: cplValue }
    const [cplLoading, setCplLoading] = useState(false);

    const userList = useSelector(getUserList);
    const userInfo = useSelector(getUserInfo);
    const CountryFilterResourcePlanning = useSelector(getCountryFilterResourcePlanningList);
    const higherOfficialsList = useSelector((state) => getHigherOfficials(state));
    const importantPeople = getEmailsByRole(higherOfficialsList, "engagementHigherUsers");

    const history = useHistory();
    const dispatch = useDispatch();
    const filteruser = userList?.filter(
        (item) => item?.userid === userInfo.userid
    );

    const isCountryAdmin =
        ["3", "8"].includes(filteruser?.[0]?.roleid?.[0]);

    const isSuperAdmin =
        ["2"].includes(filteruser?.[0]?.roleid?.[0]);
    /** ---------------- Dropdown filter API ---------------- */
    const { key: dropdownKey, url: dropdownUrl } =
        queryKeys.getEngagementFilterDropdown(
            `?managingoffice=${filteruser?.[0]?.managingoffice}&userId=${filteruser?.[0]?.userid}&subclustercountries=${filteruser?.[0]?.subclustercountries || ""}${isCountryAdmin ? `&countryadmin=true` : ""
            }${isSuperAdmin ? `&superadmin=true` : ""}`
        );


    const { data: dropdownRes } = useFetch(dropdownKey, dropdownUrl);
    const crmManagingOfficeList = useSelector(getCRMManagingOfficeList)

    const fetchAndPatchCPL = async () => {
        try {
            setCplLoading(true);

            const managingOffice = filteruser?.[0]?.managingoffice || "";
            const userId = filteruser?.[0]?.userid || "";
            const subclustercountries = filteruser?.[0]?.subclustercountries || null;

            const result = await CALL_FEATCH_API(
                `/insyts/engagementdashboardsummary?managingoffice=${encodeURIComponent(managingOffice)}&userId=${encodeURIComponent(userId)}&subclustercountries=${encodeURIComponent(subclustercountries)}`,
                "get"
            );

            const plannedData = result?.plannedData || [];
            const actualData = result?.actualTimesheet || [];

            const map = {};

            plannedData.forEach((item) => {
                const actual = actualData.find((a) => a.engagementId === item.engagementId);
                const planned = item?.plannedChargeout ?? 0;
                const actualVal = actual?.actualChargeout ?? 0;

                map[item.engagementId] =
                    planned > 0 ? parseFloat(((actualVal / planned) * 100).toFixed(1)) : 0;
            });


            setCplMap(map);
        } catch (err) {
            console.error("CPL fetch error:", err);
            message.error("Failed to load CPL data.");
        } finally {
            setCplLoading(false);
        }
    };

    // ✅ Call CPL API on mount (and whenever managing office changes)
    useEffect(() => {
        if (filteruser?.[0]?.managingoffice) {
            fetchAndPatchCPL();
        }
    }, [filteruser?.[0]?.managingoffice]);


    useEffect(() => {
        if (dropdownRes?.data) {
            const d = dropdownRes.data;
            const unique = (arr, key) =>
                [...new Set(arr.map((a) => a[key]).filter(Boolean))];
            setFilters({
                jobTitle: unique(d, "jobTitle"),
                clientName: unique(d, "clientName"),
                personIncharge: unique(d, "personIncharge"),
                partner: unique(d, "partner"),
                competency: unique(d, "competency"),
                serviceLine: unique(d, "serviceLine"),
                country: unique(d, "country"),
                engagementStatus: unique(d, "engagementStatus"),
                pfaStatus: unique(d, "pfaStatus"),
            });
            dispatch(setAccessibleEngagementIds(unique(d, "engagementId")));
        }
    }, [dropdownRes]);
    /** ---------------- Engagement APIs ---------------- */
    const baseParams = new URLSearchParams({
        managingoffice: filteruser?.[0]?.managingoffice || "",
        userId: filteruser?.[0]?.userid || "",
        subclustercountries: filteruser?.[0]?.subclustercountries || null,
        countryManagingPartner:
            filteruser?.[0]?.countryManagingPartner === "yes" ? "yes" : "no",

        ...(isCountryAdmin && { countryadmin: "true" }),
        ...(isSuperAdmin && { superadmin: "true" })
    });



    const { key: baseKey, url: baseUrl } = queryKeys.getEngagements(
        baseParams.toString()
    );

    useEffect(() => {
        form.setFieldsValue({
            engagementStatus: ["Active", "Not Started", "Not Approved"],
        });

        // Update your selectedFilters state so API gets called initially
        setSelectedFilters((prev) => ({
            ...prev,
            engagementStatus: ["Active", "Not Started", "Not Approved"],
        }));
    }, []);

    const queryString = useMemo(() => {
        const params = new URLSearchParams();

        Object.entries(selectedFilters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach((v) => {
                    if (v) params.append(key, v); // append each selected value
                });
            } else if (value) {
                params.append(key, value);
            }
        });

        if (isCountryAdmin) {
            params.append("countryadmin", "true");
        }

        if (isSuperAdmin) {
            params.append("superadmin", "true");
        }

        // Only append default managing office if country filter is NOT selected
        if (!selectedFilters.country || selectedFilters.country.length === 0) {
            params.append("country", filteruser?.[0]?.managingoffice);
        }

        // Only append default subclustercountries if subclustercountries filter is NOT selected
        if (!selectedFilters.subclustercountries || selectedFilters.subclustercountries.length === 0) {
            params.append("subclustercountries", filteruser?.[0]?.subclustercountries || "");
        }

        // Only append LastEvaluatedKey once
        if (!params.has("LastEvaluatedKey")) {
            params.append("LastEvaluatedKey", "0");
        }

        return params.toString();
    }, [selectedFilters, isCountryAdmin, isSuperAdmin, filteruser]);
    const filterFetchKey = `engagementFilter-${queryString}`;
    const { key: filterKey, url: filterUrl } = queryKeys.getEngagementFilter(`?${queryString}`);

    // Use filtered API if any filters selected
    const isFiltered = Object.keys(selectedFilters).length > 0;
    const apiKey = isFiltered ? filterFetchKey : baseKey;
    const apiUrl = isFiltered ? filterUrl : baseUrl;

    /** ---------------- Scroll + Fetch Hook ---------------- */
    const {
        data,
        isLoading: isEngagementFilterLoading,
        isReloading,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
    } = useScrollFetch2(apiKey, {
        endPoint: apiUrl,
        queryParam: "LastEvaluatedKey",
        enabled: true, // prevent re-calls unless component refreshes
    });

    const engagements = useMemo(() => {
        return (
            data?.pages?.reduce((acc, page) => {
                if (!page?.data) return acc;
                return [...acc, ...page.data];
            }, []) ?? []
        );
    }, [data?.pages]);

    /** ---------------- Infinite Scroll ---------------- */
    const handleTableScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (
            scrollTop + clientHeight >= scrollHeight - 50 &&
            hasNextPage &&
            !isFetchingNextPage
        ) {
            fetchNextPage();
        }
    };

    /** ---------------- Filter Change ---------------- */
    const handleFilterChange = (changedValues, allValues) => {
        if (Object.prototype.hasOwnProperty.call(changedValues, "jobTitle")) {
            titleModifiedRef.current = true;
        }
        const cleaned = Object.fromEntries(
            Object.entries(allValues).filter(([_, v]) => v !== undefined && v !== "")
        );
        setSelectedFilters(cleaned);
    };

    const showDeleteModal = (record) => {
        setRecordToDelete(record);
        setDeleteModalVisible(true);
    };

    const confirmDelete = async () => {
        const { clientId, engagementId, country } = recordToDelete;
        setDeletingId(engagementId);
        try {
            const { code, Message } = await CALL_API(
                `/insyts/engagement?clientId=${clientId}&engagementId=${engagementId}&country=${country}`,
                "delete"
            );

            if (code === STATUS_CODE.SUCCESS) {
                message.success(Message || "Deleted successfully");
                setLocalData((prev) =>
                    prev.filter(
                        (item) =>
                            !(
                                item.clientId === clientId &&
                                item.engagementId === engagementId &&
                                item.country.toLowerCase() === country.toLowerCase()
                            )
                    )
                );

                await refetch();
                setDeleteModalVisible(false); // Close modal after successful deletion
            } else {
                message.error(Message || "Delete failed");
            }
        } catch (err) {
            console.error("Delete failed:", err);
            message.error("Something went wrong. Please try again.");
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        if (engagements) {
            setLocalData(engagements);
        }
    }, [engagements]);

    useEffect(() => {
        if (localData.length > 0 && Object.keys(cplMap).length === 0) {
            fetchAndPatchCPL();
        }
    }, [localData]);

    const personInchargeOptions = filters.personIncharge.map((userid) => {
        const user = userList.find((u) => u.userid === userid);
        return {
            label: user?.fullname || userid, // show name, fallback to userid
            value: userid, // keep id for API
        };
    });

    const partnerOptions = filters.partner.map((userid) => {
        const user = userList.find((u) => u.userid === userid);
        return {
            label: user?.fullname || userid,
            value: userid,
        };
    });

    useEffect(() => {
        if (data?.pages) {
            const allRecords = data.pages.flatMap((p) => p.data || []);
            const uniqueCountries = [...new Set(allRecords.map((r) => r.country).filter(Boolean))];
            const uniquePfaStatus = [...new Set(allRecords.map((r) => r.pfaStatus).filter(Boolean))];
            setFilters((prev) => ({
                ...prev,
                country: uniqueCountries,
                pfaStatus: uniquePfaStatus,
            }));
        }
    }, [data]);

    useEffect(() => {
        dispatch(setTempEngagementFormData());
        dispatch(setselectedengagementtab());
    }, [localData]);


    // For Country and PFA Status (from engagements data)
    const countryOptions = filters.country.map((c) => ({ label: c, value: c }));

    const uniquemanagingOffice = uniqBy(
        crmManagingOfficeList?.map((item) => {
            return { label: item?.Label, value: item?.Label };
        }),
        "value"
    );

    const pfaOptions = [
        { label: "Not Applicable", value: "Not Applicable" },
        { label: "Approved", value: "Approved" },
        { label: "Not Approved", value: "Not Approved" },
        { label: "Declined", value: "Declined" },
    ];

    /** ---------------- Columns ---------------- */
    const columns = [
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
            width: 180,
            sorter: (a, b) => a.title.localeCompare(b.title),
            render: (val, record) => (
                <span
                    className="text-primary"
                    style={{ cursor: "pointer" }}
                    onClick={() => history.push("/engagement/view", { id: record.engagementId })}
                >
                    {val}
                </span>
            ),
        },
        {
            title: "Client Name",
            dataIndex: "clientName",
            key: "clientName",
            width: 150,
        },
        {
            title: "%CPL",
            key: "cpl",
            width: 150,
            align: "center",
            sorter: (a, b) => (cplMap[a.engagementId] ?? 0) - (cplMap[b.engagementId] ?? 0),
            render: (_, record) => {
                if (cplLoading) return <Spin size="small" />;

                // ✅ Use `!== undefined` not just truthiness, so 0% still shows
                const percent = cplMap[record.engagementId];
                if (percent === undefined) return <span>-</span>;

                return (
                    <span

                    >
                        {percent}%
                    </span>
                );
            },
        },
        {
            title: "Partner",
            dataIndex: "partner",
            key: "partner",
            width: 150,
            render: (val) => (
                <span>{find(userList, { userid: val })?.fullname || val || "-"}</span>
            ),
        },
        {
            title: "Person In Charge",
            dataIndex: "manager",
            key: "manager",
            width: 150,
            render: (val) => (
                <span>{find(userList, { userid: val })?.fullname || val || "-"}</span>
            ),
        },
        {
            title: "Country",
            dataIndex: "country",
            key: "country",
            width: 120,
        },
        {
            title: "Competency",
            dataIndex: "competency",
            key: "competency",
            width: 150,
        },
        {
            title: "Service Line",
            dataIndex: "serviceLine",
            key: "serviceLine",
            width: 150,
        },
        {
            title: "Engagement Status",
            dataIndex: "engagementStatus",
            key: "engagementStatus",
            width: 160,
        },
        {
            title: "PFA Status",
            dataIndex: "pfaStatus",
            key: "pfaStatus",
            width: 150,
        },
        {
            title: "",
            key: "delete",
            width: 50,
            render: (text, record) =>
                record.engagementStatus === "Not Started" ? (
                    <div style={{ textAlign: "center" }}>
                        <Icon
                            name="trash"
                            style={{ color: "red", cursor: "pointer" }}
                            onClick={() => showDeleteModal(record)}
                        />
                    </div>
                ) : null,
        },
    ];

    /** ---------------- UI ---------------- */
    return (
        <div>
            <Modal
                title="Are you sure you want to delete this engagement?"
                visible={deleteModalVisible}
                onOk={confirmDelete}
                onCancel={() => !deletingId && setDeleteModalVisible(false)} // Prevent closing while deleting
                okText={deletingId ? <Spin size="small" /> : "Yes"}
                cancelText="No"
                centered
                style={{ borderRadius: "30px" }}
                bodyStyle={{ fontSize: "16px", borderRadius: "30px" }}
            >
                <p>This action cannot be undone.</p>
            </Modal>
            <EngagementWrapper>
                <div className="title">
                    <Title>Engagement </Title>
                    <div>
                        <Button className="me-2"
                            type="primary"
                            onClick={() => {
                                history.push("/engagementCalendar");
                            }}
                        >
                            Calendar View
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                history.push("/add-engagement");
                            }}
                        >
                            Add New
                        </Button>
                    </div>
                </div>
                <Form form={form} layout="vertical" onValuesChange={handleFilterChange}>
                    <Row gutter={16}>
                        <Col span={6} style={{ borderRadius: 15 }}>
                            <Form.Item label="Title" name="jobTitle" >
                                <RoundedSelect
                                    mode="multiple"
                                    placeholder="Select Title"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={1}
                                    maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}

                                >
                                    {filters.jobTitle.map((t) => (
                                        <Select.Option key={t} value={t}>
                                            {t}
                                        </Select.Option>
                                    ))}
                                </RoundedSelect>
                            </Form.Item>
                        </Col>

                        <Col span={6}>
                            <Form.Item label="Client Name" name="clientName">
                                <RoundedSelect
                                    mode="multiple"
                                    placeholder="Select Client"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={1}
                                    maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                    style={{ borderRadius: 15 }}
                                >
                                    {filters.clientName.map((c) => (
                                        <Select.Option key={c} value={c}>
                                            {c}
                                        </Select.Option>
                                    ))}
                                </RoundedSelect>
                            </Form.Item>
                        </Col>

                        <Col span={6}>
                            <Form.Item label="Person In Charge" name="personIncharge">
                                <RoundedSelect
                                    mode="multiple"
                                    placeholder="Select Person"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={1}
                                    maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                    style={{ borderRadius: 15 }}
                                >
                                    {personInchargeOptions.map((opt) => (
                                        <Select.Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Select.Option>
                                    ))}
                                </RoundedSelect>
                            </Form.Item>
                        </Col>

                        <Col span={6}>
                            <Form.Item label="Partner" name="partner">
                                <RoundedSelect
                                    mode="multiple"
                                    placeholder="Select Partner"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={1}
                                    maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                    style={{ borderRadius: 15 }}
                                >
                                    {partnerOptions.map((opt) => (
                                        <Select.Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Select.Option>
                                    ))}
                                </RoundedSelect>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        {userInfo?.roleId === "2" && filteruser &&
                            importantPeople.includes(filteruser[0]?.userid) && (
                                <Col span={6}>
                                    <Form.Item label="Country" name="country">
                                        <RoundedSelect
                                            mode="multiple"
                                            placeholder="Select Country"
                                            allowClear
                                            showSearch
                                            optionFilterProp="children"
                                            maxTagCount={1}
                                            maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                            style={{ borderRadius: 15 }}
                                        >
                                            {uniqBy(
                                                CountryFilterResourcePlanning.map((item) => ({
                                                    label: item.country,
                                                    value: item.country,
                                                })),
                                                "value"
                                            )?.map((option) => (
                                                <Option key={option?.value} value={option?.value}>
                                                    {option?.label}
                                                </Option>
                                            ))}
                                        </RoundedSelect>
                                    </Form.Item>
                                </Col>
                            )}

                        <Col span={6}>
                            <Form.Item label="Competency" name="competency">
                                <RoundedSelect
                                    mode="multiple"
                                    placeholder="Select Competency"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={1}
                                    maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                    style={{ borderRadius: 15 }}
                                >
                                    {filters.competency.map((c) => (
                                        <Select.Option key={c} value={c}>
                                            {c}
                                        </Select.Option>
                                    ))}
                                </RoundedSelect>
                            </Form.Item>
                        </Col>

                        <Col span={6}>
                            <Form.Item label="Service Line" name="serviceLine">
                                <RoundedSelect
                                    mode="multiple"
                                    placeholder="Select Service Line"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={1}
                                    maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                    style={{ borderRadius: 15 }}
                                >
                                    {filters.serviceLine.map((s) => (
                                        <Select.Option key={s} value={s}>
                                            {s}
                                        </Select.Option>
                                    ))}
                                </RoundedSelect>
                            </Form.Item>
                        </Col>

                        <Col span={6}>
                            <Form.Item label="Engagement Status" name="engagementStatus">
                                <RoundedSelect
                                    mode="multiple"
                                    placeholder="Select Status"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    maxTagCount={1}
                                    maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                    style={{ borderRadius: 15 }}
                                >
                                    {["Active", "Not Approved", "Not Started", "Closed", "Draft"].map((s) => (
                                        <Select.Option key={s} value={s}>
                                            {s}
                                        </Select.Option>
                                    ))}
                                </RoundedSelect>
                            </Form.Item>
                        </Col>

                        {userInfo?.roleId !== "2" && (
                            <Col span={6}>
                                <Form.Item label="PFA Status" name="pfaStatus">
                                    <RoundedSelect
                                        mode="multiple"
                                        placeholder="Select PFA Status"
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                        maxTagCount={1}
                                        maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                        style={{ borderRadius: 15 }}
                                    >
                                        {pfaOptions.map((opt) => (
                                            <Select.Option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </Select.Option>
                                        ))}
                                    </RoundedSelect>
                                </Form.Item>
                            </Col>
                        )}
                    </Row>

                    {userInfo?.roleId === "2" && (
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label="PFA Status" name="pfaStatus">
                                    <RoundedSelect
                                        mode="multiple"
                                        placeholder="Select PFA Status"
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                        maxTagCount={1}
                                        maxTagPlaceholder={(omitted) => `+ ${omitted.length}`}
                                        style={{ borderRadius: 15 }}
                                    >
                                        {pfaOptions.map((opt) => (
                                            <Select.Option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </Select.Option>
                                        ))}
                                    </RoundedSelect>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                </Form>

                <div className="people_font_style mt-2">
                    Engagement Count: {engagements?.length}
                </div>
                <div
                    style={{
                        maxHeight: 600,
                        overflowY: "auto",
                        border: "1px solid #f0f0f0",
                        display: "flex",
                        flexDirection: "column",
                    }}
                    onScroll={handleTableScroll}
                >
                    <Table
                        columns={columns}
                        dataSource={localData}
                        rowKey={(r) => r.engagementId}
                        pagination={false}
                        loading={isEngagementFilterLoading && engagements?.length === 0}
                    // scroll={{ y: 500 }}
                    // sticky
                    />

                </div>

                {/* Loader shown for first-time or manual reload */}
                {isReloading && (
                    <div className="pagination-loader" style={{ textAlign: "center", padding: "12px 0" }}>
                        <Spin size="large" />
                    </div>
                )}
            </EngagementWrapper>

        </div>
    );
};

export default EngagementCheckNew;